import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';

/**
 * Envoi des erreurs front (HTTP 5xx, JS uncaught) vers l'API Slim.
 * Côté serveur : POST /api/errors → mail + log avec rate-limit.
 *
 * Skipped en dev local (pas de production) pour ne pas spammer.
 * Side-channel : utilise un HttpClient dédié, mais ajoute un header pour que
 * l'ErrorInterceptor puisse ignorer ses propres requêtes (anti-boucle).
 */
@Injectable({ providedIn: 'root' })
export class FrontErrorReportService {
  static readonly SKIP_HEADER = 'X-Cllaj-Skip-Error-Report';

  private lastSent: { sig: string; at: number } | null = null;

  constructor(private http: HttpClient) {}

  report(payload: {
    type: string;
    message: string;
    stack?: string;
    httpStatus?: number;
    apiUrl?: string;
  }): void {
    if (!environment.production && (environment as any).env !== 'val') {
      // dev local : on évite de spammer le serveur
      return;
    }
    if (this.shouldIgnore(payload.message)) return;

    // Anti-doublon côté client : même signature dans les 30s → skip
    const sig = (payload.type + '|' + (payload.message || '').slice(0, 200)).slice(0, 400);
    const now = Date.now();
    if (this.lastSent && this.lastSent.sig === sig && (now - this.lastSent.at) < 30000) {
      return;
    }
    this.lastSent = { sig, at: now };

    const url = (environment as any).phpApiUrl + '/errors';
    const body = {
      ...payload,
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      userId: this.getUserIdFromStorage(),
    };
    const headers = new HttpHeaders().set(FrontErrorReportService.SKIP_HEADER, '1');

    try {
      // fire-and-forget, n'attend pas la réponse
      this.http.post(url, body, { headers, responseType: 'text' as 'json' })
        .subscribe({ next: () => {}, error: () => {} });
    } catch {
      // silencieux : un échec d'envoi d'erreur ne doit jamais casser l'app
    }
  }

  private getUserIdFromStorage(): string {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) return '';
      const u = JSON.parse(raw);
      return u && (u.id || u.userId || u.email) ? String(u.id || u.userId || u.email) : '';
    } catch {
      return '';
    }
  }

  private shouldIgnore(message: string): boolean {
    if (!message) return false;
    // Bruit connu sans valeur : ResizeObserver, extensions navigateur, scripts tiers
    return /ResizeObserver loop|Non-Error promise rejection|Script error\.?$/.test(message);
  }
}
